import {produce} from 'immer';
import {useAtomCallback} from 'jotai/utils';
import {isEqual} from 'lodash';
import {useEffect, useState} from 'react';
import styled from 'styled-components';
import {getGqlErrorMessage} from '@/api/request';
import {Button} from '@/components/Button';
import toast from '@/components/HotToast';
import {ChevronDownIconComponent} from '@/components/Icons';
import {Tooltip} from '@/components/Tooltip';
import {Typography} from '@/components/Typography';
import {useGA4WithParams} from '@/hooks/tracking';
import {AnalysisItem} from '@/map/@types';
import {FootTrafficVars} from '@/map/@types/foot-traffic';
import {useFootTraffic} from '@/map/api/analytics/foot-traffic';
import {anaysisListAtom} from '@/map/atom';
import {
  FOOT_TRAFFIC_LIMIT_AREA, FOOT_TRAFFIC_LIMIT_RADIUS, FOOT_TRAFFIC_OPT_LABELS, FOOT_TRAFFIC_OPTIONS
} from '@/map/constants';
import {AreaOverlayType, FootTrafficVarTypes, FootTrafficWeekAgeTimeDayType} from '@/map/enums';
import {Divider, Flexbox, FlexCenter, interceptProps, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {omitByNilObject} from '@/utils';
import {Box, WideButton} from '../StyledComponents';
import {FootTrafficDisableBox} from './FootTrafficDisableBox';
import {FootTrafficTopPercent} from './TopPercent';

interface IProps {
  item: AnalysisItem;
}

export const FootTraffic = ({item}: IProps) => {
  const enabledFootTraffic =
    (item.type === AreaOverlayType.CIRCLE &&
      item[AreaOverlayType.CIRCLE]?.radius &&
      item[AreaOverlayType.CIRCLE]?.radius <= FOOT_TRAFFIC_LIMIT_RADIUS) ||
    (item.type === AreaOverlayType.POLYGON &&
      item[AreaOverlayType.POLYGON]?.m2 &&
      item[AreaOverlayType.POLYGON].m2 <= FOOT_TRAFFIC_LIMIT_AREA);

  if (enabledFootTraffic) {
    return <Content item={item} />;
  } else {
    return <FootTrafficDisableBox type={item.type} />;
  }
};

export const Content = ({item}: IProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [values, setValues] = useState<FootTrafficVars>({});
  const queryVars = item.footTraffic?.vars;
  const isChanged = !isEqual(queryVars, values);

  const {data, isPending, isFetching, error} = useFootTraffic(item, queryVars);

  const tracking = useGA4WithParams();

  const handleChange = (name: FootTrafficVarTypes, value: any) => {
    if (isFetching) {
      return;
    }

    if (value === 'all') {
      value = undefined;
    }

    setValues((prev) =>
      omitByNilObject({
        ...prev,
        [name]: prev[name] !== value ? value : undefined,
      })
    );
  };

  const updateFootTrafficItem = useAtomCallback(
    (get, set, next: AnalysisItem['footTraffic']) => {
      const list = get(anaysisListAtom);

      const nextList = produce(list, (draft) => {
        const idx = draft.findIndex((i) => i.id === item.id);
        if (idx >= 0) {
          draft[idx].footTraffic = {
            ...draft[idx].footTraffic,
            ...next,
          };

          if (next && next.showTile && draft[idx].delivery?.showTile) {
            (draft[idx].delivery as any).showTile = false;
          }
        }
      });

      set(anaysisListAtom, nextList);
    }
  );

  const onSubmit = () => {
    updateFootTrafficItem({vars: values});
    tracking(`floatpop_search_${item.type}`, {
      floatpop_array: values ? Object.values(values) : [],
    });
  };

  const clearVariables = () => updateFootTrafficItem({vars: undefined});

  const handleOpen = () => {
    setOpen((prev) => {
      const next = !prev;

      if (next) {
        tracking(`floatpop_open_${item.type}`);
      }

      return next;
    });
  };

  useEffect(() => {
    if (!values[FootTrafficVarTypes.day]) {
      setValues({[FootTrafficVarTypes.day]: FootTrafficWeekAgeTimeDayType.W});
    }
  }, [values]);

  useEffect(() => {
    if (isChanged) {
      setValues({...queryVars});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryVars]);

  useEffect(() => {
    if (error) {
      toast.error(getGqlErrorMessage(error));

      clearVariables();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    return () => {
      clearVariables();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.id]);

  useEffect(() => {
    const nextState = !!data || isFetching;

    if (nextState !== item.footTraffic?.showTile) {
      updateFootTrafficItem({showTile: nextState});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!data, isFetching]);

  return (
    <Root>
      <WideButton teal onClick={handleOpen}>
        <FlexCenter>
          유동인구 {open ? '닫기' : '보기'}
          <MoreButton open={open} color={palette.white} />
        </FlexCenter>
      </WideButton>
      {open && (
        <PaddingBox spacing={4}>
          {!!data && (
            <>
              <FootTrafficTopPercent data={data} />
              <PaddingBox top={8} bottom={8}>
                <Divider />
              </PaddingBox>
            </>
          )}
          {Object.entries(FOOT_TRAFFIC_OPTIONS).map(([k, options]) => {
            const key = k as FootTrafficVarTypes;
            return (
              <OptionBox key={key}>
                <Label>
                  <Flexbox>
                    {FOOT_TRAFFIC_OPT_LABELS[key]}
                    {key === FootTrafficVarTypes.time && (
                      <Tooltip
                        openOnClick
                        closeEvents={{
                          mouseout: false,
                          blur: false,
                          click: true,
                        }}
                        iconStyle={{marginLeft: 2}}
                        render={() => (
                          <div>
                            • 오전 : 6:00 ~ 10:59
                            <br />
                            • 점심 : 11:00 ~ 14:59
                            <br />
                            • 오후 : 15:00 ~ 17:59
                            <br />
                            • 저녁 : 18:00 ~ 20:59
                            <br /> • 밤 : 21:00 ~ 00:59
                            <br />* 유동량이 많지 않은 새벽 1시 ~ 5시 59분까지는
                            제외되었습니다.
                          </div>
                        )}
                      />
                    )}
                  </Flexbox>
                </Label>
                <ButtonWrapper>
                  {options?.map((i) => {
                    const active =
                      (!values[key] && i.id === 'all') || values[key] === i.id;

                    return (
                      <Button
                        key={i.id}
                        size="small"
                        variant={active ? 'fill' : 'outline'}
                        color={active ? 'primary' : 'black'}
                        onClick={() => handleChange(key, i.id)}
                      >
                        {i.label}
                      </Button>
                    );
                  })}
                </ButtonWrapper>
              </OptionBox>
            );
          })}
          <PaddingBox top={8}>
            {!!data && !isChanged ? (
              <WideButton radius disabled={isPending} onClick={clearVariables}>
                {isPending ? '분석 중...' : '초기화'}
              </WideButton>
            ) : (
              <WideButton
                radius
                disabled={
                  !values[FootTrafficVarTypes.day] || !isChanged || isFetching
                }
                onClick={onSubmit}
              >
                {isFetching ? '분석 중...' : '확인'}
              </WideButton>
            )}
          </PaddingBox>
        </PaddingBox>
      )}
    </Root>
  );
};

export const Root = styled(Box)`
  overflow: hidden;
  margin-top: 8px;
`;

export const OptionBox = styled.div`
  & + & {
    margin-top: 16px;
  }
`;

export const Label = styled(Typography).attrs({
  as: 'div',
  variant: 'medium12',
})`
  font-weight: 500;
  color: ${palette.textPrimary};
  margin-bottom: 4px;
  padding: 0 10px;
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export const MoreButton = styled(ChevronDownIconComponent).withConfig({
  shouldForwardProp: interceptProps(['open']),
})<{
  open?: boolean;
  color?: string;
}>`
  width: 16px;
  height: 16px;
  cursor: pointer;
  ${({open}) => open && `transform: rotate(180deg);`}
`;
